import React, { useState, useEffect } from 'react';
import NavLineChart from './NavLineChart';
import ReturnCalculator from './ReturnCalculator';
import 'chart.js/auto';
import { format } from 'date-fns';
import CategoryBlock from './CategoryBlock';
import RelatedProducts from './RelatedProducts';
import { PieChart } from 'react-minimal-pie-chart';
import { Chart } from 'primereact/chart';
import { Dropdown } from 'primereact/dropdown';
import axios from 'axios';
import Watchlist from './Watchlist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faHeartBroken } from '@fortawesome/free-solid-svg-icons';
import HealthInsuranceCalculator from './HealthInsuranceCalculator';
import PhoneNumberPopup from './PhoneNumberPopup';
import GoogleLoginComponent from './GoogleLoginComponent';
import EnquiryForm from './MmEnquary';
import './insurance-products.css';
import { fetchWithRetry } from './utils/api';

const CurrentDate = (date) => {
  if (!date || date == undefined) {
    date = new Date().toString();
  }
  const today = new Date(date);
  return <span>{format(today, 'dd MMM yyyy')}</span>;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const options = { day: '2-digit', month: 'short', year: '2-digit' };
  return date.toLocaleDateString('en-GB', options);
};

const NavSummaryTable = ({ navSummary }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: '2-digit' };
    return date.toLocaleDateString('en-GB', options);
  };

  // Function to calculate annualized returns
  const calculateAnnualizedReturn = (absoluteReturn, years) => {
    if (!absoluteReturn || !years || absoluteReturn <= 0) return "-";
    return ((1 + absoluteReturn / 100) ** (1 / years) - 1) * 100;
  };

  
  // Data mapping for periods
  const periods = [
    { label: "1 Week", key: "week", dateKey: "week_nav_date", valueKey: "value_invested_week", returnKey: "absolute_return_week" },
    { label: "1 Month", key: "month", dateKey: "month_nav_date", valueKey: "value_invested_month", returnKey: "absolute_return_month" },
    { label: "3 Months", key: "3month", dateKey: "3month_nav_date", valueKey: "value_invested_3month", returnKey: "absolute_return_3month" },
    { label: "6 Months", key: "6month", dateKey: "6month_nav_date", valueKey: "value_invested_6month", returnKey: "absolute_return_6month" },
    { label: "1 Year", key: "1y", dateKey: "1y_date", valueKey: "value_invested_1y", returnKey: "absolute_return_1y", years: 1 },
    { label: "3 Years", key: "3y", dateKey: "3y_date", valueKey: "value_invested_3y", returnKey: "absolute_return_3y", years: 3 },
    { label: "5 Years", key: "5y", dateKey: "5y_date", valueKey: "value_invested_5y", returnKey: "absolute_return_5y", years: 5 },
    // { 
    //   label: "Since Inception", 
    //   key: "si", 
    //   dateKey: "start_nav_date", 
    //   valueKey: "value_invested_si", 
    //   returnKey: "absolute_return_si", 
    //   years: calculateYears(navSummary.start_nav_date, latestNavDate) 
    // }
    // Add other periods as needed
  ];

  // Function to calculate years between two dates
  const calculateYears = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25); // 365.25 accounts for leap years
    return diffYears;
  };

  return (
    <table className="return-table" width="100%" cellSpacing={0} cellPadding={0}>
      <thead>
        <tr>
          <th>Period Invested for</th>
          <th>₹10000 Invested on</th>
          <th>Latest Value</th>
          <th>Absolute Returns</th>
          <th>Annualised Returns</th>
          <th>Category Avg</th>
          <th>Rank within Category</th>
        </tr>
      </thead>
      <tbody>
        {periods.map((period, index) => (
          <tr key={index}>
            <td className="robo_medium">{period.label}</td>
            <td>{formatDate(navSummary[period.dateKey])}</td>
            <td>{navSummary[period.valueKey]?.toFixed(2) || "-"}</td>
            <td className="green_text">
              {(navSummary[period.returnKey] !== null && navSummary[period.returnKey] != undefined) ? `${navSummary[period.returnKey].toFixed(2)}%` : "-"}
            </td>
            <td className="green_text">
              {/* Calculate annualized returns only for specific periods */}
              {period.years ? 
                (calculateAnnualizedReturn(navSummary[period.returnKey], period.years) !== "-" ? 
                  `${calculateAnnualizedReturn(navSummary[period.returnKey], period.years).toFixed(2)}%` 
                  : "-") 
                : "-"}
            </td>
            <td className="green_text">-</td> {/* Placeholder for Category Avg */}
            <td>-</td> {/* Placeholder for Rank within Category */}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SipAndIndexReturnChart = ({ sipReturnSummary, indexReturnSummary }) => {
  const [selectedYear, setSelectedYear] = useState(1);

  const sipDataConfig = [
    { label: "1 Year", years: 1 },
    { label: "3 Years", years: 3 },
    { label: "5 Years", years: 5 },
  ];

  // Function to get the number of SIP installments that should be counted
  const calculateNumberOfSips = (startDate, endDate) => {
    const monthsDifference = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
    // Adjust for day-of-month condition (only consider up to 12th of each month as valid SIP date)
    const validSips = endDate.getDate() > 12 ? monthsDifference + 1 : monthsDifference;
    return validSips;
  };

  const getMonthlyData = (summary, years) => {
    const latestEntry = summary.reduce((latest, entry) => {
      const entryDate = new Date(entry?.nav_date);
      return entryDate > new Date(latest?.nav_date) ? entry : latest;
    }, summary[0]);
  
    const latestDate = new Date(latestEntry?.nav_date);
    const startDate = new Date(latestDate);
    startDate.setFullYear(startDate.getFullYear() - years);
  
    // Calculate the valid SIPs
    const validSips = calculateNumberOfSips(startDate, latestDate);
  
    const monthlyData = [];
    let currentDate = new Date(startDate);
  
    while (currentDate <= latestDate && monthlyData.length < validSips) {
      const monthlyEntry = summary.reduce((closest, entry) => {
        const entryDate = new Date(entry?.nav_date);
        if (
          entryDate.getFullYear() === currentDate.getFullYear() &&
          entryDate.getMonth() === currentDate.getMonth() &&
          (!closest || Math.abs(entryDate - currentDate) < Math.abs(new Date(closest?.nav_date) - currentDate))
        ) {
          return entry;
        }
        return closest;
      }, null);
  
      monthlyData.push(monthlyEntry ? monthlyEntry?.nav_val : null);
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  
    return monthlyData;
  };
  

  const prepareChartData = (years) => {
    const sipMonthlyData = getMonthlyData(sipReturnSummary, years);
    const indexMonthlyData = getMonthlyData(indexReturnSummary, years);

    if (sipMonthlyData.length === 0 || indexMonthlyData.length === 0) {
      // Return empty data if there are no valid entries
      return {
        labels: [],
        datasets: [
          {
            label: 'SIP Returns',
            data: [],
            fill: false,
            borderColor: '#42A5F5',
            tension: 0,
            stepped: true,
          },
          {
            label: 'Index Returns',
            data: [],
            fill: false,
            borderColor: '#66BB6A',
            tension: 0,
            stepped: true,
          },
        ],
      };
    }

    const labels = sipMonthlyData.map((_, index) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (sipMonthlyData.length - index - 1));
      return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    });

    // Initialize the total units and calculate dynamic values
    let totalUnits = 0;
    let latestValue = 0;

    const sipReturns = sipMonthlyData.map((navValue) => {
      if (navValue !== null) {
        const unitsPurchased = 1000 / navValue; // Calculate units for the month
        totalUnits += unitsPurchased; // Accumulate total units
        latestValue = totalUnits * navValue; // Calculate latest value based on current NAV
      }
      return latestValue; // Return current value
    });

    totalUnits = 0;
    latestValue = 0;
    const indexReturns = indexMonthlyData.map((navValue) => {
      if (navValue !== null) {
        const unitsPurchased = 1000 / navValue; // Calculate units for the month
        totalUnits += unitsPurchased; // Accumulate total units
        latestValue = totalUnits * navValue; // Calculate latest value based on current NAV
      }
      return latestValue; // Return current value
    });

    return {
      labels,
      datasets: [
        {
          label: 'SIP Returns',
          data: sipReturns,
          fill: false,
          borderColor: '#66BB6A',
          tension: 0,
          stepped: true, // Enables the stair-step effect
        },
        {
          label: 'Index Returns',
          data: indexReturns,
          fill: false,
          borderColor: '#42A5F5',
          tension: 0,
          stepped: true, // Enables the stair-step effect
        },
      ],
    };
  };

  const chartData = prepareChartData(selectedYear);
  const documentStyle = getComputedStyle(document.documentElement);
  const textColor = documentStyle.getPropertyValue('--text-color');
  
  const chartOptions = {
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        labels: {
          color: textColor,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: 12,
        },
      },
      y: {
        ticks: {
          callback: function(value) {
            return value + ' ₹';
          },
        },
      },
    },
  };

  return (
    <div>
      <Dropdown
        style={{float: 'right',
          height: '36px',
          marginTop: '-40px',
          marginRight: '5px'}}
        value={sipDataConfig.find(option => option.years === selectedYear)} 
        options={sipDataConfig}
        onChange={(e) => {
          // console.log("e.valuee.valuee.valuee.value", e.value.years)
          setSelectedYear(e.value.years)}
        }
        optionLabel="label"
        placeholder="Select Year"
        className="mb-3"
      />
      <Chart
        style={{ width: "100%", minHeight: "300px" }}
        type="line"
        data={chartData}
        options={chartOptions}
      />
    </div>
  );
};


const SipReturnTable = ({ sipReturnSummary }) => {
  // SIP data configuration for different periods
  const sipDataConfig = [
    { label: "1 Year", years: 1 },
    { label: "3 Years", years: 3 },
    { label: "5 Years", years: 5 },
  ];

  // Function to calculate absolute return
  const calculateAbsoluteReturn = (totalInvested, currentValue) => {
    return ((currentValue - totalInvested) / totalInvested) * 100;
  };

  // Function to calculate annualized return
  const calculateAnnualizedReturn = (absoluteReturn, years) => {
    return ((1 + absoluteReturn / 100) ** (1 / years) - 1) * 100;
  };

  // Determine the latest date in sipReturnSummary
  const latestEntry = sipReturnSummary.reduce((latest, entry) => {
    const entryDate = new Date(entry.nav_date);
    return entryDate > new Date(latest.nav_date) ? entry : latest;
  }, sipReturnSummary[0]);

  if (!latestEntry || !latestEntry.nav_val) {
    return;
  }

  const latestNav = latestEntry.nav_val;
  const latestDate = new Date(latestEntry.nav_date);

  // Function to get the closest date entry for each month within the given range
  const getMonthlyData = (startDate, endDate, maxMonths) => {
    const monthlyData = [];
    let currentDate = new Date(startDate);
  
    while (currentDate <= endDate && monthlyData.length < maxMonths) {
      // Get the closest entry for the month
      const monthlyEntry = sipReturnSummary.reduce((closest, entry) => {
        const entryDate = new Date(entry.nav_date);
        // Check if the entry is in the same month and year
        if (
          entryDate.getFullYear() === currentDate.getFullYear() &&
          entryDate.getMonth() === currentDate.getMonth() &&
          (!closest || Math.abs(entryDate - currentDate) < Math.abs(new Date(closest.nav_date) - currentDate))
        ) {
          return entry;
        }
        return closest;
      }, null);
  
      // Add the entry or '-' if not found
      monthlyData.push(monthlyEntry ? monthlyEntry : '-');
      currentDate.setMonth(currentDate.getMonth() + 1); // Move to next month
    }
  
    return monthlyData;
  };
  

  // Calculate SIP return values dynamically based on sipReturnSummary
  const sipData = sipDataConfig.map((config) => {
    const { years, label } = config;
  
    // Calculate the start date by subtracting the required years from the latest date
    const startDate = new Date(latestDate);
    startDate.setFullYear(startDate.getFullYear() - years);
  
    // Determine the maximum number of months (12 for 1 year, 36 for 3 years, 60 for 5 years)
    const maxMonths = years * 12; // 12 months per year
  
    // Get monthly data from startDate to latestDate, limited to maxMonths
    const monthlyData = getMonthlyData(startDate, latestDate, maxMonths);
  
    // Check if there is missing data for any month in the range
    const hasMissingData = monthlyData.some(entry => entry === '-');
  
    // If there is missing data, return '-' for all values
    if (hasMissingData) {
      return {
        label,
        startDate: "-",
        totalInvestment: "-",
        latestValue: "-",
        absoluteReturn: "-",
        annualisedReturn: "-"
      };
    }
  
    // Calculate cumulative units and total investment
    let totalUnits = 0;
    let totalInvested = 0;
  
    // Limit SIP calculations to exact number of SIPs (maxMonths)
    monthlyData.forEach((entry, index) => {
      if (index < maxMonths) {
        const unitsPurchased = 1000 / entry.nav_val;
        totalUnits += unitsPurchased;
        totalInvested += 1000;
      }
    });
  
    // Calculate the latest value using the total units and latest NAV
    const latestValue = totalUnits * latestNav;
  
    // Calculate returns
    const absoluteReturn = calculateAbsoluteReturn(totalInvested, latestValue);
    const annualisedReturn = calculateAnnualizedReturn(absoluteReturn, years);
  
    return {
      label,
      startDate: startDate.toISOString(),
      totalInvestment: totalInvested,
      latestValue,
      absoluteReturn,
      annualisedReturn,
    };
  });
  

  // Function to format date in 'dd-MMM-yy' format
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: '2-digit' };
    return date.toLocaleDateString('en-GB', options);
  };

  return (
    <table className="mctable1" width="100%" cellSpacing={0} cellPadding={0}>
      <thead>
        <tr>
          <th align="left">Period Invested for</th>
          <th align="center">₹1000 SIP Started on</th>
          <th align="center">Investments</th>
          <th align="center">Latest Value</th>
          <th align="center">Absolute Returns</th>
          <th align="center">Annualised Returns</th>
        </tr>
      </thead>
      <tbody>
        {sipData.map((period, index) => (
          <tr key={index}>
            <td className="robo_medium">{period.label}</td>
            <td>{period.startDate === '-' ? '-' : formatDate(period.startDate)}</td>
            <td>{period.totalInvestment === '-' ? '-' : period.totalInvestment.toLocaleString()}</td>
            <td>{period.latestValue === '-' ? '-' : period.latestValue.toFixed(2)}</td>
            <td className="green_text">
              {period.absoluteReturn === '-' ? '-' : `${period.absoluteReturn.toFixed(2)} %`}
            </td>
            <td className="green_text">
              {period.annualisedReturn === '-' ? '-' : `${period.annualisedReturn.toFixed(2)} %`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};


const Popup = ({ isOpen, onClose, title, list }) => {
  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div style={{textAlign: "flex"}}>
          <div style={{width: "50%", float: 'left'}}>
            <h2>{title}</h2> 
          </div>
          <div style={{textAlign: "right", width: "50%", float: 'left'}}>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
        <ul>
          {list.map((item, index) => (
            // <li key={index}>{item.name} - {item.per}%</li>
            <>
              <li>
                <span className="holding-name">
                  {item.name}
                  {(item.ISIN) && <span style={{marginLeft: '5px', color: '#9a9393'}}>ISIN: {item.ISIN}</span>}
                </span>
                <span className="holding-per" style={{float: "right"}}>{parseFloat(item.per || 0).toFixed(2)}%</span>
              </li>
              <div className="holding-bar">
                <div className="bar" style={{ width: `${item.per || 0}%` }}></div>
              </div>
            </>
          ))}
        </ul>
        
      </div>
    </div>
  );
};

// class PhoneNumberPopup {
//   constructor(props) {
//     super(props);
//     this.state = {
//       phoneNumber: '',
//       errorMessage: '',
//     };
//   }

//   handleInputChange = (event) => {
//     this.setState({ phoneNumber: event.target.value });
//   };

//   handleSubmit = async (event) => {
//     event.preventDefault();
//     const { phoneNumber } = this.state;

//     if (!/^\d{10}$/.test(phoneNumber)) {
//       this.setState({ errorMessage: 'Please enter a valid 10-digit phone number' });
//       return;
//     }

//     try {
//       const response = await fetch('https://api.example.com/submit', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({ phoneNumber }),
//       });
//       const data = await response.json();
//       console.log('API Response:', data);

//       // Close popup on success
//       this.props.togglePopup();
//       this.setState({ phoneNumber: '', errorMessage: '' });
//     } catch (error) {
//       console.error('API call failed:', error);
//       this.setState({ errorMessage: 'Failed to submit phone number. Please try again.' });
//     }
//   };

//   render() {
//     if (!this.props.showPopup) return null; // Only render if showPopup is true

//     return (
//       <div className="popup">
//         <div className="popup-inner">
//           <h2>Enter Phone Number</h2>
//           <form onSubmit={this.handleSubmit}>
//             <input
//               type="text"
//               value={this.state.phoneNumber}
//               onChange={this.handleInputChange}
//               placeholder="Phone Number"
//             />
//             <button type="submit">Submit</button>
//             <button type="button" onClick={this.props.togglePopup}>Cancel</button>
//           </form>
//           {this.state.errorMessage && <p className="error">{this.state.errorMessage}</p>}
//         </div>
//       </div>
//     );
//   }
// }

class ProductDetail extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            product: null,
            products: null,
            loading: true,
            error: null,
            holdings: [],
            sectors: [],
            holdingsOpen: true,
            sectorsOpen: true,
            additional_data: null,
            nav_summary: null,
            sip_return_summary: null,
            index_return_summary: null,
            compareProductData: null,
            compareAdditionalData: null,
            productsList: [],
            selectedCompareProduct: '',
            compareLoading: false,
            summaryData: [],
            showAllHoldings: false,
            showAllSectors: false,
            selectedCategory: 'equity',
            hovered1:undefined,
            hovered2:undefined,
            hovered:undefined,
            showPopup: false,
            showLoginPopup: false,
            popupTitle: '',
            popupList: [],
            snapshot_data: [],
            compareList: [],
            showFeatures: false,
            showVideo: false
        };
    }

    componentDidMount() {
        this.fetchProductData();
    }

    toggleLoginPopup = () => {
      this.setState({ showLoginPopup: !this.state.showLoginPopup });
      // console.log("this.state.showLoginPopup", this.state.showLoginPopup)
    };
    
    async fetchProductData() {
        const { config } = this.props;
        const productCode = config.productCode;
        const apiUrl = `${config.PY_API_URL}category/products/${encodeURIComponent(productCode)}?userId=${config.userId}`;

        try {
            const data = await fetchWithRetry(apiUrl);
            
            if (data && data.data && data.data.product) {
                const summaryData = data.data?.summary_data || [];
                this.setState({
                    product: data.data.product,
                    products: data.data.variants,
                    nav_summary: data.data.nav_summary,
                    sip_return_summary: data.data.sip_return_summary,
                    index_return_summary: data.data.index_return_summary,
                    additional_data: data.data?.additional_data,
                    snapshot_data: data.data?.snapshot_data,
                    summaryData,
                    loading: false,
                });
                
                document.title = data.data.product.name;
            } else {
                this.setState({ 
                    loading: false, 
                    error: `Sorry, we couldn't find the products you're looking for. Please explore other available options.` 
                });
            }
        } catch (error) {
            console.error('Error fetching product data:', error);
            this.setState({ 
                loading: false, 
                error: 'Unable to fetch product data. Please try again later.' 
            });
        }
    }


    handleViewAllClick = (type) => {
      const list = type === 'sectors' ? this.getTopSectors() : this.getTopHoldings();
      this.setState({
        showPopup: true,
        popupTitle: type === 'sectors' ? 'All Sectors' : 'All Holdings',
        popupList: list
      });
    };

    getTopHoldings = () => {
      const { summaryData, selectedCategory} = this.state;
      const equityData = summaryData.filter(item => item.pcc === 'equity');
      const debtData = summaryData.filter(item => item.pcc === 'debt');
      const otherData = summaryData.filter(item => item.pcc === 'others');
      const filteredData = selectedCategory === 'equity' ? equityData : 
                            selectedCategory === 'debt' ? debtData : otherData;
      return filteredData.sort((a, b) => parseFloat(b.per) - parseFloat(a.per));
    };
  
    getTopSectors = () => {
      const { summaryData, selectedCategory} = this.state;
      const equityData = summaryData.filter(item => item.pcc === 'equity');
      const debtData = summaryData.filter(item => item.pcc === 'debt');
      const otherData = summaryData.filter(item => item.pcc === 'others');
      const filteredData = selectedCategory === 'equity' ? equityData : 
                            selectedCategory === 'debt' ? debtData : otherData;

      const sectorGroups = filteredData.reduce((acc, item) => {
        acc[item.cat] = acc[item.cat] || [];
        acc[item.cat].push(item);
        return acc;
      }, {});

      return Object.keys(sectorGroups).map(cat => ({
        name: cat,
        per: sectorGroups[cat].reduce((sum, item) => sum + parseFloat(item.per), 0),
      })).sort((a, b) => b.per - a.per);
    };
  
    closePopup = () => {
      this.setState({ showPopup: false });
    };

    setSelectedProduct = (e) => {
      const {products} = this.state;
      const [selectedProduct] = products.filter(item => item.scheme_code === e.target.value)
      this.setState({
        product: selectedProduct
      });
      this.fetchProductDetails(e.target.value);
    }

    fetchProductDetails = async (schemeCode) => {
        if (!schemeCode || schemeCode == undefined) {
            return;
        }

        this.setState({
            nav_summary: {
                "cagr": "",
                "latest_nav_date": "",
                "latest_nav_val": "",
                "nav_diff_latest_and_prev": "",
                "start_nav_date": "",
                "start_nav_val": ""
            },
            summaryData: [],
        });

        const { config } = this.props;
        const apiUrl = `${config.PY_API_URL}category/products/${encodeURIComponent(config.productCode)}?schemeCode=${schemeCode}`;

        try {
            const data = await fetchWithRetry(apiUrl);
            
            if (data && data.data && data.data.product) {
                const summaryData = data.data?.summary_data || [];
                this.setState({
                    nav_summary: data.data.nav_summary,
                    index_return_summary: data.data.index_return_summary,
                    sip_return_summary: data.data.sip_return_summary,
                    snapshot_data: data.data?.snapshot_data,
                    summaryData,
                    loading: false,
                });
                document.title = data.data.product.name;
            }
        } catch (error) {
            console.error('Error fetching product data:', error);
            this.setState({ 
                loading: false, 
                error: 'Error fetching product data. Please try again later.' 
            });
        }
    }

    addToCompare = (productToCompare) => {
      const { compareList } = this.state;
      const { product } = this.state;
      if (compareList.some((p) => p.product_code === productToCompare.product_code)) {
        this.setState({
          compareList: compareList.filter((p) => p.product_code !== productToCompare.product_code),
        });
      } else {
        if (compareList.length >= 2) {
          this.setState({
            compareList: [compareList[0], productToCompare],
          });
        } else {
          let list = [...compareList, productToCompare]
          if (compareList.length == 0 && !compareList.some((p) => p.product_code === product.product_code)) {
            list.push(product);
          }
          this.setState({
            compareList: list,
          });
        }
      }
    };

    removeFromCompare = (product) => {
      this.setState((prevState) => ({
        compareList: prevState.compareList.filter((p) => p.product_code !== product.product_code),
      }));
    };

    compareNow = () => {
      const { compareList } = this.state;
      const { config } = this.props;
      if (compareList.length < 2) {
        alert('Please select at least 2 products to compare.');
        return;
      }
      const compareItems = compareList.map(item => `${item.product_code}:${item.scheme_code}`).join('-vs-');
      window.location.href = `https://app.moneymarche.com/app/compare/${compareItems}`;
    };

    renderInsuranceDetails() {
        const { product } = this.state;
        if (!product || product.product_type_code !== 2) return null;

        // Helper function to parse nested JSON values
        const getJsonValue = (key) => {
            const attr = product.attributes?.find(attr => attr.attribute_key === key);
            if (!attr) return null;
            try {
                return JSON.parse(attr.attribute_value);
            } catch (e) {
                console.error(`Error parsing ${key}:`, e);
                return null;
            }
        };

        // Helper function to get simple attribute value
        const getAttributeValue = (key) => {
            return product.attributes?.find(attr => attr.attribute_key === key)?.attribute_value;
        };

        const bannerInfo = getJsonValue('Banner Info');
        const coverageDetails = getJsonValue('Coverage Details');
        const features = getJsonValue('Features');
        const allFeatures = getJsonValue('All Features');
        const videoUrl = getAttributeValue('Video URL');

        const handleFeatureClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.setState({ showFeatures: true });
        };

        const handleVideoClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.setState({ showVideo: true });
        };

        return (
            <>
                <div className="insurance-product-details" style={{width:'100%', marginBottom: '20px'}}>
                    <div className="insurance-banner">
                        {bannerInfo && Object.entries(bannerInfo).map(([key, value]) => (
                            <div key={key} className={`banner-${key}`}>
                                <i className={`fas ${value.icon}`}></i>
                                <span>{value.text}</span>
                            </div>
                        ))}
                    </div>

                    <div className="insurance-header">
                        <div className="coverage-details">
                          {
                            (coverageDetails?.amount) && (
                              <div>
                                <div className="coverage-label">Cover amount</div>
                                <div className="coverage-amount">
                                    {coverageDetails?.amount}
                                </div>
                            </div>
                          )
                        }
                        {
                          (coverageDetails?.premium) && (
                            <div>
                                <div className="starting-label">Starting at</div>
                                <div className="premium-amount">
                                    {coverageDetails?.premium}
                                </div>
                            </div>
                          )
                        }
                        </div>
                        {
                          (coverageDetails?.discount) && (
                            <div className="online-discount">
                              Inclusive of {coverageDetails?.discount}
                            </div>
                          )
                        }
                    </div>

                    <div className="features-list">
                        {features?.map((feature, index) => (
                            <div key={index} className={`feature-item ${feature.isPrimary ? 'heart' : ''}`}>
                                <i className={`fas ${feature.icon}`}></i>
                                <span>{feature.text}</span>
                            </div>
                        ))}
                    </div>

                    <div className="action-links">
                        {
                          (features?.length > 0) && (
                            <a href="#" onClick={handleFeatureClick} className="action-link">
                                View all features <i className="fas fa-chevron-right"></i>
                            </a>
                          )
                        }
                        {videoUrl && videoUrl != '' && (
                            <a href="#" onClick={handleVideoClick} className="action-link">
                                Watch plan Video <i className="fas fa-play"></i>
                            </a>
                        )}
                    </div>
                </div>

                {/* Additional Insurance Details */}
                <div className="insurance-additional-details card" style={{padding: '20px', marginBottom: '20px'}}>
                    <h3 style={{marginBottom: '20px', color: '#2f3374'}}>Policy Details</h3>
                    <div className="details-grid" style={{
                        display: 'table',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '20px'
                    }}>
                        {[
                            { key: 'Plan Options', icon: 'fa-list-alt' },
                            { key: 'Premium Payment Terms', icon: 'fa-calendar-alt' },
                            { key: 'Policy Term', icon: 'fa-clock' },
                            { key: 'Entry Age', icon: 'fa-user' },
                            { key: 'Maturity Age', icon: 'fa-birthday-cake' },
                            { key: 'Sum Assured', icon: 'fa-shield-alt' },
                            { key: 'Survival Benefit', icon: 'fa-hand-holding-usd' },
                            { key: 'Death Benefit', icon: 'fa-heart' },
                            { key: 'Optional Benefits', icon: 'fa-plus-circle' },
                            { key: 'Premium Payment Modes', icon: 'fa-money-bill-wave' },
                            { key: 'Tax Benefits', icon: 'fa-file-invoice-dollar' },
                            { key: 'Min Premium', icon: 'fa-dollar-sign' }
                        ].map(item => {
                            const value = getAttributeValue(item.key);
                            if (!value) return null;
                            
                            return (
                                <div key={item.key} style={{
                                    padding: '15px',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    borderLeft: '4px solid #2f3374',
                                    marginBottom: '10px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        marginBottom: '8px'
                                    }}>
                                        <i className={`fas ${item.icon}`} style={{color: '#2f3374'}}></i>
                                        <strong style={{color: '#666'}}>{item.key}</strong>
                                    </div>
                                    <div style={{color: '#333', fontSize: '14px'}}>{value}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Fund Statistics */}
                {/* <div className="insurance-stats card" style={{padding: '20px', marginBottom: '20px'}}>
                    <h3 style={{marginBottom: '20px', color: '#2f3374'}}>Fund Statistics</h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '20px'
                    }}>
                        {[
                            { key: 'AUM', label: 'Assets Under Management', icon: 'fa-chart-line' },
                            { key: 'TER', label: 'Total Expense Ratio', icon: 'fa-percentage' }
                        ].map(item => {
                            const value = getAttributeValue(item.key);
                            if (!value) return null;

                            return (
                                <div key={item.key} style={{
                                    padding: '15px',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <i className={`fas ${item.icon}`} style={{
                                        fontSize: '24px',
                                        color: '#2f3374',
                                        marginBottom: '10px'
                                    }}></i>
                                    <div style={{color: '#666', fontSize: '13px'}}>{item.label}</div>
                                    <div style={{
                                        fontSize: '18px',
                                        color: '#2f3374',
                                        fontWeight: '600',
                                        marginTop: '5px'
                                    }}>{value}</div>
                                </div>
                            );
                        })}
                    </div>
                </div> */}

                {/* Popups */}
                {this.state.showFeatures && (
                    <div className="features-popup" onClick={() => this.setState({ showFeatures: false })}>
                        <div className="features-popup-content" onClick={e => e.stopPropagation()}>
                            <button 
                                className="popup-close" 
                                style={{padding: '10px', lineHeight: '10px'}} 
                                onClick={() => this.setState({ showFeatures: false })}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                            <h3>All Features</h3>
                            {allFeatures?.map((feature, index) => (
                                <div key={index}>
                                    <h4>{feature.title}</h4>
                                    <p>{feature.value}</p>
                                </div>
                            ))}
                            <div className="popup-footer">
                                <button 
                                    onClick={() => this.setState({ showFeatures: false })}
                                    style={{
                                        padding: '10px 24px',
                                        background: '#2196f3',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {this.state.showVideo && videoUrl && (
                    <div className="features-popup" onClick={() => this.setState({ showVideo: false })}>
                        <div className="features-popup-content" onClick={e => e.stopPropagation()}>
                            <button 
                                className="popup-close" 
                                style={{padding: '10px', lineHeight: '10px'}} 
                                onClick={() => this.setState({ showVideo: false })}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                            <iframe 
                                width="100%" 
                                height="315" 
                                src={videoUrl} 
                                frameBorder="0" 
                                allowFullScreen
                            />
                            <div className="popup-footer">
                                <button 
                                    onClick={() => this.setState({ showVideo: false })}
                                    style={{
                                        padding: '10px 24px',
                                        background: '#2196f3',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    render() {
        const { product, loading, error, additional_data, summaryData,
          selectedCategory, hovered, showPopup, popupTitle, popupList, 
          nav_summary, products, sip_return_summary, index_return_summary, 
          snapshot_data, showLoginPopup} = this.state;
        const { config } = this.props;

        const { compareList } = this.state;

        if (loading) {
            return <p>Loading...</p>;
        }

        const relatedProductConfig = {
          "product_code": product?.product_code || "360-ONE-Focused-Equity-Fund",
          "productLayout": "table",
          "categoryName": product?.category_code || 'mutual-funds',
          "categoryId": product?.category_id,
          "PY_API_URL": config.PY_API_URL,
          "siteUrl": config.siteUrl,
          "showBannerImage": false,
          "showTitle": false,
          "showShortDescription": false,
          "showLongDescription": false,
          "additionalSettings": false,
          "fundPerformance": false,
          "product_type_code": product?.product_type_code
        }

        // Existing enquiry form configuration
        const enquiryFormConfig = {
            ...config,
            renderInline: true,
            showCategoryForm: true,
            productCode: product?.scheme_code,
            categoryCode: product?.category_code,
            // Existing configurations
        };

        // Create a separate enquiry form configuration for inline rendering
        const enquiryFormConfigInline = {
            ...enquiryFormConfig, // Use the existing config as a base
            showCategoryForm: true, // Ensure the category form is shown
            buttonText: "Book a Callback", // Custom button text
            buttonClass: "full-width-button", // Custom CSS class for full width
            renderInline: true,
            hideHeaderSection: true,
            hideCardContainer: true,
            buttonStyle: JSON.stringify({
                width: '100%',
                textAlign: 'center',
                backgroundColor: '#6200ea',
                color: '#fff',
                borderRadius: '5px',
                cursor: 'pointer',
            }),
            // Add any other specific configurations needed for inline rendering
        };

        if (error) {
          return (<div>
            <div className="elementor-widget-container mm-bred">
                <ul className="elementor-icon-list-items elementor-inline-items">
                <li className="elementor-icon-list-item elementor-inline-item bred-item">
                  <a href={config.siteUrl}>
                    <span className="elementor-icon-list-text">Home</span>
                  </a>
                </li>
              </ul>
            </div>

            <div className="elementor-container elementor-column-gap-extended">            
              <div
                className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-8c98556"
                data-id="8c98556"
                data-element_type="column" style={{minHeight: '100px', display: 'block'}}
              >
                <p>{error}</p>
                <div className="elementor-widget-wrap elementor-element-populated">
                  <div
                    className="elementor-element elementor-element-16c79d1 elementor-widget elementor-widget-image"
                    data-id="16c79d1"
                    data-element_type="widget"
                  >
                    <div className="product-return-chart" style={{marginBottom: '20px'}}>                        
                      <NavLineChart productCode={product?.scheme_code} default_index={product?.default_index} config={config} />
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-cd9a2db"
                data-id="cd9a2db"
                data-element_type="column"
              >
                <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-widget-container widget-wrapper">
                      <ReturnCalculator initialReturns={nav_summary?.cagr} />
                    </div>
                    <div className="elementor-widget-container widget-wrapper" style={{marginTop:'0px', fontSize: '12px'}}>
                      <RelatedProducts  config={relatedProductConfig} />
                    </div>                    
                </div>
              </div>
            </div>
          </div>)
          // return <p>{error}</p>;
        }

        const equityData = summaryData.filter(item => item.pcc === 'equity');
        const debtData = summaryData.filter(item => item.pcc === 'debt');
        const otherData = summaryData.filter(item => item.pcc === 'others');

        let totalEquityPercent = equityData.reduce((sum, item) => sum + parseFloat(item.per), 0) || 0;
        let totalDebtPercent = debtData.reduce((sum, item) => sum + parseFloat(item.per), 0) || 0;
        let totalOtherPercent = otherData.reduce((sum, item) => sum + parseFloat(item.per), 0) || 0;

        if (totalEquityPercent < 0) {totalEquityPercent = 0}
        if (totalDebtPercent < 0) {totalDebtPercent = 0}
        totalOtherPercent = 100 - (totalEquityPercent + totalDebtPercent);
        if (totalOtherPercent < 0) {totalOtherPercent = 0}

        const filteredData = selectedCategory === 'equity' ? equityData : 
                            selectedCategory === 'debt' ? debtData : otherData;

        const typeGroups = Object.assign({
          large_cap: 0,
          mid_cap: 0,
          other_cap: 0,
          small_cap: 0,
        }, filteredData.reduce((acc, item) => {
          acc[item.f_type] = acc[item.f_type] || 0;
          acc[item.f_type] = parseFloat(acc[item.f_type]) + parseFloat(item.per);
          return acc;
        }, {}));

        const topHoldings = filteredData.sort((a, b) => parseFloat(b.per) - parseFloat(a.per)).slice(0, 4);
        const sectorGroups = filteredData.reduce((acc, item) => {
          acc[item.cat] = acc[item.cat] || [];
          acc[item.cat].push(item);
          return acc;
        }, {});

        const topSectors = Object.keys(sectorGroups)
          .map(cat => ({
            cat,
            totalPer: sectorGroups[cat].reduce((sum, item) => sum + parseFloat(item.per), 0),
          }))
          .sort((a, b) => b.totalPer - a.totalPer)
          .slice(0, 4);

        if (additional_data && additional_data.length > 0) {
          data = additional_data[0];
        }

        const typeChartData = [
          { title: 'Large Cap', value: parseFloat(typeGroups.large_cap.toFixed(2)), color: '#2ca02c' },
          { title: 'Mid Cap', value: parseFloat(typeGroups.mid_cap.toFixed(2)), color: '#ff7f0e' },
          { title: 'Small Cap', value: parseFloat(typeGroups.small_cap.toFixed(2)), color: '#d62728' },
          { title: 'Other Cap', value: parseFloat(typeGroups.other_cap.toFixed(2)), color: '#ffbb78' }
        ];

        const createDropDown = function (item) {
          return <option value={item.scheme_code}>{item.name}</option>;
        }

        const toggleWatchlist = async () => {
            try {
                const { config } = this.props;
                const { product, products } = this.state;

                if (product.inWatchlist) {
                    await fetchWithRetry(`${config.PY_API_URL}member/watchlist/delete`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ 
                            user_id: config.userId, 
                            product_id: product.id 
                        })
                    });

                    this.setState({
                        product: {
                            ...product,
                            inWatchlist: 0,
                        },
                        products: products.map(p =>
                            p.scheme_code === product.scheme_code ? { ...p, inWatchlist: 0 } : p
                        )
                    });
                } else {
                    await fetchWithRetry(`${config.PY_API_URL}member/watchlist`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ 
                            user_id: config.userId, 
                            product_id: product.id 
                        })
                    });

                    this.setState({
                        product: {
                            ...product,
                            inWatchlist: 1,
                        },
                        products: products.map(p =>
                            p.scheme_code === product.scheme_code ? { ...p, inWatchlist: 1 } : p
                        )
                    });
                }
            } catch (error) {
                console.error('Error updating watchlist:', error);
                // Optionally show an error message to the user
                this.setState({ 
                    error: 'Failed to update watchlist. Please try again.' 
                });
            }
        };

        

        return (
            <div>
              <div className="elementor-widget-container mm-bred">
                  <ul className="elementor-icon-list-items elementor-inline-items">
                  <li className="elementor-icon-list-item elementor-inline-item bred-item">
                    <a href={config.siteUrl}>
                      <span className="elementor-icon-list-text">Home</span>
                    </a>
                  </li>
                  {product?.p_category_code &&
                    <li className="elementor-icon-list-item elementor-inline-item bred-item">
                      {product.p_category_custom_url && 
                        <a href={`${product.p_category_custom_url}`}>
                          {' » '} <span className="elementor-icon-list-text">{product.p_category_name || ''}</span>
                        </a>
                      }
                      {!product.p_category_custom_url && 
                        <a href={`${config.siteUrl}/category/${product?.p_category_code}`}>
                          {' » '} <span className="elementor-icon-list-text">{product.p_category_name || ''}</span>
                        </a>
                      }       
                    </li>
                  }
                  <li className="elementor-icon-list-item elementor-inline-item bred-item">
                    {product.category_custom_url && 
                      <a href={`${product.category_custom_url}`}>
                        {' » '} <span className="elementor-icon-list-text">{product.category_name || ''}</span>
                      </a>
                    }
                    {!product.category_custom_url && 
                      <a href={`${config.siteUrl}/category/${product.category_code}`}>
                        {' » '} <span className="elementor-icon-list-text">{product.category_name || ''}</span>
                      </a>
                    }                
                  </li>
                  <li className="elementor-icon-list-item elementor-inline-item bred-item">
                      {' » '} <span className="elementor-icon-list-text">{product.name || ''}</span>
                  </li>
                </ul>
                {
                  products.length > 1 && <div className='product-variants'>
                    <select onChange={this.setSelectedProduct}>{products.map(createDropDown)}</select>
                  </div>
                }

                {config.userId != 0 && <button onClick={toggleWatchlist} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <FontAwesomeIcon title={product.inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'} icon={product.inWatchlist ? faHeart: faHeartBroken} size="2x" color={product.inWatchlist ? 'red': 'gray'} />
                </button>}
                {/* {!config.userId && <div style={{width: '210px', maxWidth: '210px', float: 'left'}}><GoogleLoginComponent config={config}/></div>} */}
              </div>

              <div className="elementor-container elementor-column-gap-extended"> 

                <div className="fund-card product-page-header">
                
                  <div className="fund-info">
                    <a href={`${config.siteUrl}/amc/${product.owner_code}`}>
                      <img 
                        src={product.banner_image || 'https://placehold.co/1200x400'} 
                        alt="Fund Logo" 
                        className="fund-logo" 
                      />
                    </a>
                    <div className="fund-details">
                    <a href={`${config.siteUrl}/amc/${product.owner_code}`}>
                      <h1 className="fund-name">
                        {product.name || ''}
                      </h1>
                      </a>
                      <div className="fund-tags">
                        {product?.p_category_code && 
                          <span className="tag">                        
                            {product.p_category_custom_url && 
                              <a href={`${product.p_category_custom_url}`}>{product.p_category_name || ''}</a>
                            }
                            {!product.p_category_custom_url && 
                              <a href={`${config.siteUrl}/category/${product?.p_category_code}`}>{product.p_category_name || ''}</a>
                            }
                          </span>
                        }
                        <span className="tag">                        
                          {product.category_custom_url && 
                            <a href={`${product.category_custom_url}`}>{product.category_name || ''}</a>
                          }
                          {!product.category_custom_url && 
                            <a href={`${config.siteUrl}/category/${product.category_code}`}>{product.category_name || ''}</a>
                          }
                        </span>       
                        { (nav_summary && nav_summary.start_nav_date && !product.launch_date) && <div style={{fontSize: '14px'}}>
                            Start Date: {CurrentDate(nav_summary?.start_nav_date)}
                          </div>
                        }
                        { (product && product.launch_date) && <div style={{fontSize: '14px'}}>
                            Start Date: {CurrentDate(product.launch_date)}
                          </div>
                        }          
                        
                      </div>
                    </div>
                  </div>
                  {
                    nav_summary?.latest_nav_val && <div className="fund-nav">
                      <span className="nav-date">NAV as of {CurrentDate(product?.last_nav_date ? product?.last_nav_date : new Date().toString())}</span>
                      <div className="nav-value">
                        { 
                          (nav_summary?.latest_nav_val > 0) && 
                          <span>₹{parseFloat(nav_summary?.latest_nav_val).toFixed(2) || ''}</span> 
                        }
                        
                        { 
                          (nav_summary?.nav_diff_latest_and_prev && nav_summary?.nav_diff_latest_and_prev > 0) && 
                          <span className="nav-change posetive">+{parseFloat(nav_summary?.nav_diff_latest_and_prev).toFixed(2) || ''}%</span> 
                        }
                        { 
                          (nav_summary?.nav_diff_latest_and_prev && nav_summary?.nav_diff_latest_and_prev < 0) && 
                          <span className="nav-change negative">{parseFloat(nav_summary?.nav_diff_latest_and_prev).toFixed(2) || ''}%</span> 
                        }
                        { 
                          (nav_summary?.nav_diff_latest_and_prev && nav_summary?.nav_diff_latest_and_prev == 0) && 
                          <span className="nav-change negative-">{parseFloat(nav_summary?.nav_diff_latest_and_prev).toFixed(2) || ''}%</span> 
                        }
                        { 
                          (nav_summary?.nav_diff_latest_and_prev) && 
                          <span style={{fontSize:'14px', fontWeight: 'normal'}}>1D change</span>
                        }
                        
                      </div>
                    </div>
                  }

                  { nav_summary?.cagr && <div className="fund-returns">
                      <span>Returns since inception</span>
                      <div className="returns-value">
                        <span style={{fontSize: '18px', lineHeight: 'normal'}}>{nav_summary?.cagr | '-'}</span>
                        <span style={{fontSize: '12px'}}> p.a</span>
                      </div>
                      <marquee width="240px" direction="up" height="18px" scrollamount="1">
                        <ul style={{listStyle: 'none', color: '#28a02b', textAlign: 'right'}}>
                          <li style={{padding: '0px'}}>
                            1 Year Absolute Return: {nav_summary?.absolute_return_1y ? nav_summary?.absolute_return_1y.toFixed(2) : "-"}
                          </li>
                          <li style={{padding: '0px'}}>
                            3 Year Absolute Return: {nav_summary?.absolute_return_3y ? nav_summary?.absolute_return_3y.toFixed(2) : "-"}
                          </li>
                          <li style={{padding: '0px'}}>
                            5 Year Absolute Return: {nav_summary?.absolute_return_5y ? nav_summary?.absolute_return_5y.toFixed(2) : "-"}
                          </li>
                        </ul>
                      </marquee>
                    </div>
                  }
                </div>                
                <div
                  className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-8c98556"
                  data-id="8c98556"
                  data-element_type="column" style={{minHeight: '100px', display: 'block'}}
                >

                {
                  (['insurance'].includes(product?.p_category_code)) && <HealthInsuranceCalculator config={config} product={product}/>
                }

                {product?.product_type_code == 2 && <div className="booking-widget-inline-container">
                  {this.renderInsuranceDetails()}
                </div>}

                {(product?.product_type_code == 1) && 
                  <div>
                    <ul className='product-stats card' style={{padding: '10px', 'listStyle': 'none'}}>
                      {product?.scheme_minimum_amount && <li>
                          <b>Min Investment Amount:</b> {product?.scheme_minimum_amount || "N/A"}
                      </li>}
                      {product?.scheme_type && <li>
                          <b>Scheme Type:</b> {product?.scheme_type || 'N/A'}
                      </li>}
                      <li>
                          <b>Total AUM:</b> {product?.aum || 'N/A'}
                      </li>
                      <li>
                          <b>Launch date:</b> {formatDate(product.launch_date) || 'N/A'}
                      </li>
                      { 
                        product.closure_date && <li>
                          <b>Closure:</b> {formatDate(product.closure_date) || 'N/A'}
                        </li>
                      }
                      <li>
                          <b>Bench mark: </b> {product?.bench_mark || "N/A"}
                      </li>
                      <li>
                          <b>Expense ratio: </b> {product?.expense_ratio || "N/A"}
                      </li>
                      {product?.crisil_rating && <li>
                          <b>Crisil Rating: </b> {product?.crisil_rating || "N/A"}
                      </li>}
                      {product?.alpha && <li>
                          <b>Alpha: </b> {product?.alpha || "N/A"}
                      </li>}
                      {product?.beta && <li>
                          <b>Beta: </b> {product?.beta || "N/A"}
                      </li>}
                      {product?.sharpe_ratio && <li>
                          <b>Sharpe Ratio: </b> {product?.sharpe_ratio || "N/A"}
                      </li>}
                      {product?.treynors_ratio && <li>
                          <b>Treynor's Ratio: </b> {product?.treynors_ratio || "N/A"}
                      </li>}
                      {product?.jensions_alpha && <li>
                          <b>Jension's Alpha: </b> {product?.jensions_alpha || "N/A"}
                      </li>}
                      <li>
                          <b>Scheme Objective: </b> {product?.scheme_objective || "N/A"}
                      </li>
                      {product?.fund_managers && <li>
                          <b>Fund manager(s): </b> {product?.fund_managers || "N/A"}
                      </li>}
                      {product?.suitable_for && <li>
                          <b>Suitable For: </b> {product?.suitable_for || "N/A"}
                      </li>}
                      {product?.snapshot && <li>
                          <b>Snapshot: </b> {product?.snapshot || "N/A"}
                      </li>}
                      {product?.risk_meter_scheme && <li>
                          <b> Risk meter: </b> {product?.risk_meter_scheme || "N/A"}
                      </li>}
                      {product?.risk_meter_benchmark && <li>
                          <b> Benchmark risk meter: </b> {product?.risk_meter_benchmark || "N/A"}
                      </li>
                      }
                    </ul>
                  </div>
                  }
                  
                  {product.product_type_code == 2 && <div className="booking-widget-inline-container">
                    {/* <EnquiryForm config={enquiryFormConfig} /> */}
                  </div>
                  }

                  <div className="elementor-widget-wrap elementor-element-populated">
                  {
                    nav_summary?.latest_nav_val &&
                    <div
                      className="elementor-element elementor-element-16c79d1 elementor-widget elementor-widget-image"
                      data-id="16c79d1"
                      data-element_type="widget"
                    >
                      <div className="product-return-chart" style={{marginBottom: '20px'}}>                        
                        <NavLineChart productCode={product.scheme_code} default_index={product.default_index} config={config} />
                      </div>
                    </div>
                   
                  }

                  {/* {snapshot_data && snapshot_data.length > 0 && ( <ul style={{listStyle: 'none', padding: '15px'}} class="elementor-widget-container widget-wrapper">
                        {snapshot_data.map((item, index) => (
                          <li key={index}>
                            <strong>{item.key_title}:</strong> {item.key_value}  */}
                            {/* {item.key_addl_info && <span> ({item.key_addl_info})</span>} */}
                          {/* </li>
                        ))}
                      </ul>
                  )} */}

                  {snapshot_data && snapshot_data.length > 0 && (
                    <table style={{ width: '100%', padding: '15px', borderCollapse: 'collapse' }} className="elementor-widget-container widget-wrapper">
                      <thead>
                        <tr>
                          <th colSpan={2} style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>Scheme Details</th>
                          {/* <th style={{ borderBottom: '1px solid #ddd', padding: '8px', width: '30%' }}>Title</th>
                          <th style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>Value</th> */}
                          {/* Uncomment the following line if you want to display additional info */}
                          {/* <th style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>Additional Info</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {snapshot_data.map((item, index) => (
                          <tr key={index}>
                            <td style={{ borderBottom: '1px solid #ddd', padding: '8px', width: '30%'  }}>{item.key_title}</td>
                            <td style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>{item.key_value}</td>
                            {/* Uncomment the following if additional info needs to be displayed */}
                            {/* {item.key_addl_info && <td style={{ borderBottom: '1px solid #ddd', padding: '8px' }}>({item.key_addl_info})</td>} */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}


                  {product.short_description && (
                    <div className="widget-wrapper" style={{padding: '10px', marginTop: '20px'}}>
                      <p dangerouslySetInnerHTML={{ __html: product.short_description || '' }} />
                    </div>
                  )}

                  {product.description && (
                    <div className="widget-wrapper" style={{padding: '10px', marginTop: '20px'}}>
                      <p dangerouslySetInnerHTML={{ __html: product.description || '' }} />
                    </div>
                  )}
                    {/* <div className="widget-wrapper">
                      <ul className="elementor-icon-list-items">
                        <li className="elementor-icon-list-item">
                          <span className="elementor-icon-list-icon">
                            <i aria-hidden="true" className="fas fa-caret-up" />{" "}
                          </span>
                          <span className="elementor-icon-list-text">
                            <b>Return Consistency:</b> This fund is one of the best
                            in generating consistent return in it's category.
                          </span>
                        </li>

                        <li className="elementor-icon-list-item">
                          <span className="elementor-icon-list-icon">
                            <i aria-hidden="true" className="fas fa-caret-up" />{" "}
                          </span>
                          <span className="elementor-icon-list-text">
                            <b>Return Consistency:</b> This fund is one of the best
                            in generating consistent return in it's category.
                          </span>
                        </li>
                        <li className="elementor-icon-list-item">
                          <span className="elementor-icon-list-icon">
                            <i aria-hidden="true" className="fas fa-caret-up" />{" "}
                          </span>
                          <span className="elementor-icon-list-text">
                            <b>Return Consistency:</b> This fund is one of the best
                            in generating consistent return in it's category.
                          </span>
                        </li>
                        <li className="elementor-icon-list-item">
                          <span className="elementor-icon-list-icon">
                            <i aria-hidden="true" className="fas fa-caret-up" />{" "}
                          </span>
                          <span className="elementor-icon-list-text">
                            <b>Return Consistency:</b> This fund is one of the best
                            in generating consistent return in it's category.
                          </span>
                        </li>
                        <li className="elementor-icon-list-item">
                          <span className="elementor-icon-list-icon">
                            <i aria-hidden="true" className="fas fa-caret-up" />{" "}
                          </span>
                          <span className="elementor-icon-list-text">
                            <b>Return Consistency:</b> This fund is one of the best
                            in generating consistent return in it's category.
                          </span>
                        </li>
                        <li className="elementor-icon-list-item">
                          <span className="elementor-icon-list-icon">
                            <i aria-hidden="true" className="fas fa-caret-up" />{" "}
                          </span>
                          <span className="elementor-icon-list-text">
                            <b>Return Consistency:</b> This fund is one of the best
                            in generating consistent return in it's category.
                          </span>
                        </li>
                      </ul>
                    </div> */}


                    {/* <div className="elementor-widget-container widget-wrapper">
                      <table className='alternate-color'>
                        <tbody>
                          <tr>
                            <th colSpan="3">
                              <p><span style={tableHeaderStyle}>Trailing returns % (Point to Point)</span></p>
                            </th>
                          </tr>
                          <tr className='head2'>
                            <td>&nbsp;</td>
                            <td>
                              <p><span>Value</span></p>
                            </td>
                            <td>
                              <p><span>Rank</span></p>
                            </td>
                          </tr>
                          {["1 year", "2 year", "3 year", "5 year", "7 year", "10 year", "12 year"].map((period, index) => (
                            <tr key={index}>
                              <td>
                                <p><span>{period}</span></p>
                              </td>
                              <td>
                                <p><span>{formatNumber(data?.["Compounded Annualised % (Point to Point)"]?.[`${period.replace(" ", "_")}`]) || "–"}</span></p>
                              </td>
                              <td>
                                <p><span>{data?.["Compounded Annualised % (Point to Point)"]?.[`${period.replace(" ", "_")}_rank`] || "–"}</span></p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div> */}

                    
                    {summaryData.length > 0 && (
                      <div className="elementor-widget-container widget-wrapper">
                        <div className="portfolio-summary">
                          <div className="asset-allocation">
                            <div className='widget-head'>
                              <div className='widget-head-left'>
                                <h3>Asset allocation</h3>
                                <p className="date">as on {CurrentDate(product?.last_nav_date ? product?.last_nav_date : new Date().toString())}</p>
                              </div>
                              <div className='widget-head-right'>
                                <div className="allocation-breakdown">
                                  <div className={`allocation-type ${this.state.selectedCategory === 'equity' ? 'active' : ''}`}
                                    onClick={() => this.setState({ selectedCategory: 'equity' })}>
                                    <span className="allocation-label equity">Equity</span>
                                    <span className="allocation-value">{parseFloat(totalEquityPercent).toFixed(2)}%</span>
                                  </div>
                                  <div className={`allocation-type ${this.state.selectedCategory === 'debt' ? 'active' : ''}`}
                                    onClick={() => this.setState({ selectedCategory: 'debt' })}>
                                    <span className="allocation-label debt">Debt</span>
                                    <span className="allocation-value">{parseFloat(totalDebtPercent).toFixed(2)}%</span>
                                  </div>
                                  <div className={`allocation-type ${this.state.selectedCategory === 'others' ? 'active' : ''}`}>
                                    <span className="allocation-label other">Other</span>
                                    <span className="allocation-value">{parseFloat(totalOtherPercent).toFixed(2)}%</span>
                                  </div>
                                </div>

                              </div>
                            </div>

                            <div className='holding-chart-widget'>
                              <div className="allocation-details">
                                <div className="market-cap-chart">
                                  <PieChart
                                    data={typeChartData}
                                    onMouseOver={(_, index) => this.setState({ hovered: index })}
                                    onMouseOut={() => this.setState({ hovered: undefined })}
                                    // radius={42}  // Adjust the radius if needed
                                    innerRadius={20}  // This makes it a donut chart
                                    lineWidth={50}  // Adjust the line width to control the thickness of the donut
                                    labelPosition={112}  // Position of the labels
                                    animate  // Optional: enables animation
                                  />
                                  <div style={{ position: 'absolute', width:'74px', textAlign: 'center'}}>
                                    {hovered !== undefined && (
                                      <>
                                        {typeChartData[hovered].title}: {typeChartData[hovered].value}%
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="market-cap-breakdown">
                                <ul>
                                {parseFloat(typeGroups.large_cap) > 0 && (
                                    <li>
                                      <span className="large-cap color-circle"></span>
                                      Large Cap {parseFloat(typeGroups.large_cap).toFixed(2)}%
                                    </li>
                                  )}

                                  {parseFloat(typeGroups.mid_cap) > 0 && (
                                    <li>
                                      <span className="mid-cap color-circle"></span>
                                      Mid Cap {parseFloat(typeGroups.mid_cap).toFixed(2)}%
                                    </li>
                                  )}

                                  {parseFloat(typeGroups.small_cap) > 0 && (
                                    <li>
                                      <span className="small-cap color-circle"></span>
                                      Small Cap {parseFloat(typeGroups.small_cap).toFixed(2)}%
                                    </li>
                                  )}

                                  {parseFloat(typeGroups.other_cap) > 0 && (
                                    <li>
                                      <span className="other-cap color-circle"></span>
                                      Other Cap {parseFloat(typeGroups.other_cap).toFixed(2)}%
                                    </li>
                                  )}
                                </ul>
                              </div>
                            </div>
                            
                          </div>
                          
                          <div className="holdings-sectors">
                            <div className="top-holdings">
                              <h3>Top holdings</h3>
                              <ul>
                              {topHoldings.map((holding, index) => (
                                <>
                                  <li>
                                    <span className="holding-name">{holding.name}</span>
                                    <span className="holding-per">{parseFloat(holding.per || 0).toFixed(2)}%</span>
                                  </li>
                                  <div className="holding-bar">
                                    <div className="bar" style={{ width: `${holding.per || 0}%` }}></div>
                                  </div>
                                </>
                                ))}               
                              </ul>
                              {filteredData.length > 4 && (
                                <span className='view-all-cta' onClick={() => this.handleViewAllClick('holdings')}>View all</span>
                              )}
                            </div>
                            
                            <div className="top-sectors">
                              <h3>Top sectors</h3>
                              <ul>
                                {topSectors.map((sector, index) => (
                                  <>
                                    <li>
                                      <span className="sector-name">{sector.cat}</span>
                                      <span className="sector-per">{parseFloat(sector.totalPer || 0).toFixed(2)}%</span>
                                    </li>
                                    <div className="sector-bar">
                                      <div className="bar" style={{ width: `${sector.totalPer || 0}%`, backgroundColor: '#A27FFF' }}></div>
                                    </div>
                                  </>
                                ))}
                                
                              </ul>
                              {Object.keys(sectorGroups).length > 4 && (
                                <span className='view-all-cta' onClick={() => this.handleViewAllClick('sectors')}>View all</span>
                              )}
                            </div>
                          </div>                          
                        </div>
                      </div>
                    )}

                    {
                      (nav_summary && nav_summary.latest_nav_val) && <>
                        <div className="widget-wrapper">
                          <h4 className="title_24px CTR title_botline">
                            Returns{" "}
                            <span className="paragraph" style={{fontSize: '18px'}}>(NAV as on {CurrentDate(product?.last_nav_date ? product?.last_nav_date : new Date().toString())})</span>
                          </h4>
                          {/* <p class="paragraph TAC"> Investment Value is latest value of Rs 10,000 invested for the period. SIP vale is latest value of Rs 100 SIP started on every 1st of the month from the start date. Rank is based on performance in the period among 134 Large Cap Funds </p> */}
                          <div className="data_container returns_table table-responsive">
                            <NavSummaryTable navSummary={nav_summary} />
                          </div>
                          
                        </div>
                      </>
                    }
                    {
                      (sip_return_summary && sip_return_summary.length > 0) && <>
                        <div className="widget-wrapper">
                          <h4 className="title_24px CTR title_botline">
                            SIP Return Summary{" "}
                            <span className="paragraph" style={{fontSize: '18px'}}>(NAV as on {CurrentDate(product?.last_nav_date ? product?.last_nav_date : new Date().toString())})</span>
                          </h4>
                          <div className='table-responsive'>
                            <SipReturnTable sipReturnSummary={sip_return_summary} />
                          </div>
                        </div>

                        <div className="widget-wrapper">
                          <h4 className="title_24px CTR title_botline">
                            SIP Return Chart
                          </h4>
                          <div className='table-responsive'>
                          <SipAndIndexReturnChart 
                            sipReturnSummary={sip_return_summary} 
                            indexReturnSummary={index_return_summary} 
                          />
                          </div>
                        </div>
                      </>
                    }
                    

                  </div>
                </div>
                <div
                  className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-cd9a2db"
                  data-id="cd9a2db"
                  data-element_type="column"
                >
                
                  <div className="elementor-widget-wrap elementor-element-populated">
                      
                      {/* show enquiry form button for product type code 1  */}
                      {/* {(['investments', 'mutual-funds', 'equity', 'hybrid', 'debt'].includes(product?.p_category_code)) && ( */}
                        <div className="elementor-widget-container widget-wrapper" style={{padding: '20px', overflow: 'hidden'}}>
                          <div className="enquiry-text" style={{background: '#683dcc', padding: '10px', marginLeft: '-20px', marginRight: '-20px', marginTop: '-20px', color: '#fff', fontSize: '16px'}}>
                            <p style={{margin: '0px'}}>Take a free appointment with our advisor.</p>
                          </div>
                          <EnquiryForm config={enquiryFormConfigInline} />
                        </div>
                      {/* )} */}
                        
                    {/* Return Calculator */}
                      {
                        (['investments', 'mutual-funds', 'equity', 'hybrid', 'debt'].includes(product?.p_category_code)) && <div className="elementor-widget-container widget-wrapper">
                          <ReturnCalculator initialReturns={nav_summary?.cagr} />
                        </div>
                      }

                      {/* Related Products */}
                      <div className="elementor-widget-container widget-wrapper" style={{marginTop:'0px', fontSize: '12px'}}>
                        <RelatedProducts 
                        type={"related"} config={relatedProductConfig} 
                        compareList={compareList}
                        addToCompare={this.addToCompare}
                        removeFromCompare={this.removeFromCompare}
                        />
                      </div>

                      <div className="elementor-widget-container widget-wrapper" style={{marginTop:'0px', fontSize: '12px'}}>
                        <RelatedProducts title={"Same AMC/Other Products"} type={"same_amc"} config={relatedProductConfig} 
                          compareList={compareList}
                          addToCompare={this.addToCompare}
                          removeFromCompare={this.removeFromCompare}
                        />
                      </div>  

                      <div className="elementor-widget-container widget-wrapper" style={{marginTop:'0px', fontSize: '12px'}}>
                        <RelatedProducts title={"Other Category Top Products"} type={"other_caregory"} config={relatedProductConfig} 
                          compareList={compareList}
                          addToCompare={this.addToCompare}
                          removeFromCompare={this.removeFromCompare}
                        />
                      </div>    

                  </div>
                </div>
              </div>
              <Popup
                isOpen={showPopup}
                onClose={this.closePopup}
                title={popupTitle}
                list={popupList}
              />

              <PhoneNumberPopup 
                config={config}
                showPhonePopup={showLoginPopup}
                togglePopup={this.toggleLoginPopup}
              />
              {/* Sticky Compare List */}
              {compareList.length > 0 && (
                <div className="sticky-compare-list">
                  <h4>Compare Products</h4>
                  <ul>
                  {compareList.map((product) => (
                    <li key={product.product_code}>
                      <div>
                        {product.name}
                        <span className='product-card' onClick={() => this.removeFromCompare(product)}>&times;</span>
                      </div>
                    </li>
                  ))}
                  </ul>
                  {compareList.length > 1 && (
                    <button onClick={this.compareNow}>Compare Now</button>
                  )}
                </div>
              )}
            </div>
        );
    }
}

export default ProductDetail;
